import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { PasswordField } from "@majitap/ui/arc/password-field/password-field";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import {
  configOf,
  connectionActions,
  type Field,
  formFor,
  INTEGRATION_STATUS,
  integrationsQuery,
  KIND,
  type ProviderForm,
  providerFormsQuery,
  secretsOf,
  secretsReady,
} from "../../connections";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import styles from "./settings.module.css";

export const integrationFilters = filterSearch({ quickbooks: "text" });

/** What came back from Intuit, as the API names it in `?quickbooks=`. */
export function quickbooksResult(code: string | undefined): { tone: "success" | "danger"; text: string } | null {
  if (!code) return null;
  if (code === "connected")
    return { tone: "success", text: "QuickBooks is connected. New invoices and payments sync." };
  if (code === "access_denied" || code === "denied")
    return { tone: "danger", text: "QuickBooks wasn't connected: the sign-in at Intuit was cancelled." };
  if (code === "invalid_state")
    return { tone: "danger", text: "QuickBooks wasn't connected: the link expired. Start again with Connect." };
  return { tone: "danger", text: `QuickBooks wasn't connected: Intuit answered “${code}”. Try Connect again.` };
}

/** Integrations (P8-23): the company's M-Pesa, SMS, email, QuickBooks, eTIMS and sensor accounts. */
export function IntegrationsPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/integrations" });
  const list = useQuery(integrationsQuery);
  const forms = useQuery(providerFormsQuery);
  const [adding, setAdding] = useState(false);
  const result = quickbooksResult(search.quickbooks);
  return (
    <>
      <PageHeader
        title="Integrations"
        description="The accounts MajiTap uses to take payments, send messages, keep the books and report tax."
        actions={<Button onClick={() => setAdding(true)}>Add an integration</Button>}
      />
      {result ? (
        <Alert tone={result.tone} title={result.tone === "success" ? "Connected" : "Not connected"}>
          {result.text}
        </Alert>
      ) : null}
      {list.status === "pending" ? (
        <PageLoading label="Loading integrations" />
      ) : list.status === "error" ? (
        <PageError message={messageFor(errorOf(list.error))} onRetry={() => list.refetch()} />
      ) : (
        <SortableDataTable
          caption="Integrations"
          rowKey="id"
          rows={list.data}
          itemName={{ one: "integration", other: "integrations" }}
          columns={[
            {
              key: "name",
              label: "Name",
              sortable: false,
              render: (_v, i) => (
                <span className={styles.stack}>
                  <Link
                    to="/settings/integrations/$integrationId"
                    params={{ integrationId: i.id }}
                    className={styles.link}
                  >
                    {i.name}
                  </Link>
                  <span className={styles.muted}>{formFor(forms.data ?? [], i).label}</span>
                </span>
              ),
            },
            { key: "kind", label: "For", sortable: false, render: (_v, i) => KIND[i.kind] },
            {
              key: "status",
              label: "Status",
              sortable: false,
              render: (_v, i) => {
                const [label, tone] = INTEGRATION_STATUS[i.status];
                return (
                  <span className={styles.actions}>
                    <Badge tone={tone}>{label}</Badge>
                    {i.is_sandbox ? <Badge tone="warning">Test account</Badge> : null}
                  </span>
                );
              },
            },
            {
              key: "secret_keys",
              label: "Credentials",
              sortable: false,
              render: (_v, i) => (i.secret_keys.length ? "Stored" : <span className={styles.danger}>Not set</span>),
            },
          ]}
        />
      )}
      {adding && forms.data ? <AddDialog forms={forms.data} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

/**
 * A provider's settings and credentials, from the form the API describes. Credentials are
 * write-only: the fields start empty and never show what's stored.
 */
export function IntegrationFields({
  form,
  config,
  secrets,
  onConfig,
  onSecrets,
  stored = [],
  picks,
}: {
  form: ProviderForm;
  config: Record<string, string>;
  secrets: Record<string, string>;
  onConfig: (next: Record<string, string>) => void;
  onSecrets: (next: Record<string, string>) => void;
  /** The names of the stored secrets, when editing. */
  stored?: string[];
  /** What the connected service has, for fields picked from it; typed instead while it's missing. */
  picks?: Partial<Record<NonNullable<Field["pick"]>["from"], PickOption[]>>;
}) {
  const help = (f: Field) =>
    [f.help, f.type === "list" ? "Separate them with commas." : undefined].filter(Boolean).join(" ") || undefined;
  return (
    <>
      {form.note ? <p className={styles.muted}>{form.note}</p> : null}
      {form.config.length ? (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Settings</legend>
          {form.config.map((f) => {
            const label = f.required ? f.label : `${f.label} (optional)`;
            const options = f.pick ? picks?.[f.pick.from] : undefined;
            if (f.pick && options) {
              return (
                <Select
                  key={f.key}
                  label={label}
                  description={f.help}
                  placeholder="Pick one"
                  value={config[f.key] || NONE}
                  onValueChange={(v) => onConfig({ ...config, [f.key]: v === NONE ? "" : v })}
                  options={pickOptions(options, f.pick.types, config[f.key] ?? "", !f.required)}
                />
              );
            }
            return (
              <Input
                key={f.key}
                label={label}
                description={help(f)}
                value={config[f.key] ?? ""}
                onChange={(e) => onConfig({ ...config, [f.key]: e.target.value })}
              />
            );
          })}
        </fieldset>
      ) : null}
      {form.secrets.length ? (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Credentials</legend>
          <p className={styles.muted}>
            {stored.length
              ? "Stored, and never shown. Leave these empty to keep them. To change one, type them all again: saving replaces every stored credential."
              : "Stored encrypted, and never shown again."}
          </p>
          {form.secrets.map((f) => (
            <PasswordField
              key={f.key}
              label={f.required ? f.label : `${f.label} (optional)`}
              description={f.help}
              autoComplete="new-password"
              placeholder={stored.includes(f.key) ? "Stored" : ""}
              value={secrets[f.key] ?? ""}
              onChange={(e) => onSecrets({ ...secrets, [f.key]: e.target.value })}
            />
          ))}
        </fieldset>
      ) : null}
    </>
  );
}

export type PickOption = { value: string; label: string; type: string | null };
const NONE = "__none";

/**
 * A picked field's choices: of the given types, "None" when it's optional, and the stored value
 * kept when the service no longer has it, so saving doesn't drop it unseen.
 */
function pickOptions(options: PickOption[], types: string[] | undefined, current: string, optional: boolean) {
  const shown = types ? options.filter((o) => o.type && types.includes(o.type)) : options;
  return [
    ...(optional ? [{ value: NONE, label: "None" }] : []),
    ...(current && !shown.some((o) => o.value === current)
      ? [{ value: current, label: `ID ${current} (not found)` }]
      : []),
    ...shown.map((o) => ({ value: o.value, label: o.type && types ? `${o.label} · ${o.type}` : o.label })),
  ];
}

/** Pick a provider, name it, fill its settings and credentials. It starts off until turned on. */
function AddDialog({ forms, onClose }: { forms: ProviderForm[]; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<string>();
  const [name, setName] = useState("");
  const [sandbox, setSandbox] = useState(false);
  const [config, setConfig] = useState<Record<string, string>>({});
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const form = forms.find((f) => `${f.kind}:${f.provider}` === choice);
  const ready =
    form &&
    name.trim() &&
    form.config.every((f) => !f.required || (config[f.key] ?? "").trim()) &&
    form.secrets.every((f) => !f.required || (secrets[f.key] ?? "").trim()) &&
    secretsReady(form.secrets, secrets);
  const create = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("no provider");
      return connectionActions.create({
        provider: form.provider,
        kind: form.kind,
        name: name.trim(),
        is_sandbox: sandbox,
        status: "inactive",
        config: configOf(form.config, config),
        secrets: secretsOf(form.secrets, secrets),
      });
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onClose();
      void navigate({ to: "/settings/integrations/$integrationId", params: { integrationId: created.id } });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Add an integration" description="It starts off. Turn it on from its page once it's set up.">
        <div className={styles.form}>
          <Select
            label="Provider"
            placeholder="Choose one"
            value={choice}
            onValueChange={(v) => {
              setChoice(v);
              setConfig({});
              setSecrets({});
            }}
            options={forms.map((f) => ({
              value: `${f.kind}:${f.provider}`,
              label: `${f.label} · ${KIND[f.kind]}`,
            }))}
          />
          {form ? (
            <>
              <div className={styles.pair}>
                <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Main paybill" />
              </div>
              <Switch label="A test (sandbox) account" checked={sandbox} onCheckedChange={setSandbox} />
              <IntegrationFields
                form={form}
                config={config}
                secrets={secrets}
                onConfig={setConfig}
                onSecrets={setSecrets}
              />
            </>
          ) : null}
          {create.error ? (
            <Alert tone="danger" title="Not added">
              {messageFor(errorOf(create.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!ready} loading={create.isPending} onClick={() => create.mutate()}>
              Add it
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
